/**
 * MeteredUsageView — the Usage page for a workspace billed on the rate card
 * (`billingContract === "metered"`, every workspace today). Written to be
 * forwarded: the month-to-date headline (billed for developers and Agents
 * together, beside the same work at Claude list), then the ledger for the
 * picked range: 01 Developers, 02 Teams and Agents, 03 Activation, and 04
 * Credits, plan and guardrails.
 *
 * ADR-0028: sell-side numbers only, admin-gated server-side. Every figure is
 * read from the `/api/yagni-code-usage` endpoints the report always read.
 */
import { useMemo, useState, type ReactNode } from "react";

import { Btn, Chip, Fold, ListHead } from "../queue/index.js";
import { CreditsSection } from "./CreditsSection.js";
import { DevelopersSection } from "./DevelopersSection.js";
import { TeamsAndAgentsSection, type FetchTeamAgentUsage } from "./TeamsAndAgentsSection.js";
import {
  CLIENT_LABELS,
  RANGE_PRESETS,
  activationStatus,
  customRange,
  presetRange,
  usageCsvHref,
  useLedger,
  type Ledger,
  type RangePreset,
} from "./ledger.js";
import {
  fetchTeamAgentUsage,
  memberDisplayName,
  monthLabel,
  organizationHeadline,
  rangeLabel,
  usageNarrative,
  usd,
  useUsageReport,
  type UsageRange,
} from "./usageReport.js";
import { Bar2, Key, Legend, Note, Quiet, Sec, Tile, Tiles } from "./usageParts.js";
import { savedWithShare } from "./usageGlass.js";

const AGENTS_COLOR = "var(--stage-review)";

export function MeteredUsageView({
  previewSwitch,
  fetchTeamUsage = fetchTeamAgentUsage,
}: {
  previewSwitch?: ReactNode;
  fetchTeamUsage?: FetchTeamAgentUsage;
}) {
  const now = useMemo(() => new Date(), []);
  const month = monthLabel(now);
  const [preset, setPreset] = useState<RangePreset | "custom">("this-month");
  const [range, setRange] = useState<UsageRange>(() => presetRange("this-month", now));
  const [customOpen, setCustomOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState("");
  const [draftThrough, setDraftThrough] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  const ledger = useLedger(range);

  const pick = (next: RangePreset) => {
    setPreset(next);
    setCustomOpen(false);
    setDraftError(null);
    setRange(presetRange(next));
  };
  const applyCustom = (event: React.FormEvent) => {
    event.preventDefault();
    const next = customRange(draftFrom, draftThrough);
    if (typeof next === "string") {
      setDraftError(next);
      return;
    }
    setDraftError(null);
    setPreset("custom");
    setRange(next);
  };

  const chips = (
    <>
      {RANGE_PRESETS.map(({ key, label }) => (
        <Chip key={key} tone={preset === key ? "on" : undefined} onClick={() => pick(key)}>
          {label}
        </Chip>
      ))}
      <Chip tone={preset === "custom" || customOpen ? "on" : undefined} onClick={() => setCustomOpen((open) => !open)}>
        {preset === "custom" ? `Custom · ${rangeLabel(range)}` : "Custom"}
      </Chip>
      {customOpen && (
        <form onSubmit={applyCustom} className="field" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <label htmlFor="usage-range-from">From</label>
          <span className="in" style={{ height: 26 }}>
            <input id="usage-range-from" type="date" value={draftFrom} onChange={(event) => setDraftFrom(event.target.value)} />
          </span>
          <label htmlFor="usage-range-through">through</label>
          <span className="in" style={{ height: 26 }}>
            <input
              id="usage-range-through"
              type="date"
              value={draftThrough}
              onChange={(event) => setDraftThrough(event.target.value)}
            />
          </span>
          <Btn type="submit" variant="secondary" size="sm">
            Apply
          </Btn>
          {draftError && (
            <span role="alert" className="fact" style={{ color: "var(--honesty)" }}>
              {draftError}
            </span>
          )}
        </form>
      )}
    </>
  );

  return (
    <>
      <ListHead
        title={`Usage · ${month}`}
        sub={["Metered on the rate card · four lanes, the models behind them rotate, the rate does not", "Written to be forwarded"]}
        acts={
          <>
            {previewSwitch}
            <a className="btn secondary sm" href={usageCsvHref(range)} download>
              Download CSV
            </a>
          </>
        }
        chips={chips}
        picked={`Days are UTC · as of ${now.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
      />
      <Headline month={month} />
      <DevelopersSection range={range} ledger={ledger} />
      {ledger.reportState !== "forbidden" && (
        <>
          <TeamsAndAgentsSection
            range={range}
            fetchTeamUsage={fetchTeamUsage}
            workerUsage={ledger.workers}
            workerUsageError={ledger.workersError}
          />
          <ActivationSection ledger={ledger} now={now} />
          <CreditsSection />
        </>
      )}
    </>
  );
}

/** The month-to-date headline: the sentence, the four tiles, and the split bar. Never follows the range. */
function Headline({ month }: { month: string }) {
  const { report, agents, error, loading } = useUsageReport({ withActivation: false });
  if (loading) return <Note>Reading the ledger</Note>;
  if (error || !report) return <Note tone="honest">{error ?? "Failed to load usage."}</Note>;
  const headline = organizationHeadline(report, agents);
  const agentsShare =
    headline.agentsMillicents !== null && headline.billedMillicents > 0
      ? Math.round((100 * headline.agentsMillicents) / headline.billedMillicents)
      : 0;
  const devShare = 100 - agentsShare;
  return (
    <>
      <p className="brief" data-testid="usage-narrative">
        {usageNarrative(report, month, headline)}
      </p>
      <Tiles testId="usage-headline">
        <Tile
          value={usd(headline.billedMillicents)}
          label="Billed"
          sub={
            headline.agentsMillicents === null
              ? "developers only"
              : `developers ${usd(headline.developersMillicents)} · Agents ${usd(headline.agentsMillicents)}`
          }
        />
        <Tile value={usd(headline.claudeMillicents)} label="At Claude list" tone="quiet" sub="same tokens, matched tier" />
        <Tile
          value={savedWithShare(headline.savedMillicents, headline.percent, usd)}
          label="Saved"
          tone="green"
          sub="vs Claude list"
        />
        <Tile
          value={usd(headline.promptWritingMillicents)}
          label="Claude prompt writing"
          tone="quiet"
          sub="included in Claude list · 1.25× cache write"
          title="From October 6, 2026, Claude writes its prompt to a 5 minute cache on every turn, at 1.25x the input price (before then, only reported cache writes carry it). That premium is part of the Claude list figure."
        />
      </Tiles>
      {headline.agentsMillicents !== null && headline.developersMillicents > 0 && headline.agentsMillicents > 0 && (
        <div data-testid="usage-split">
          <Bar2
            label={`Developers ${usd(headline.developersMillicents)} (${devShare}%), Agents ${usd(headline.agentsMillicents)} (${agentsShare}%)`}
            parts={[
              { share: devShare, className: "k-bl" },
              { share: agentsShare, color: AGENTS_COLOR },
            ]}
          />
          <Legend style={{ marginTop: 6 }}>
            <Key className="k-bl">developers {usd(headline.developersMillicents)}</Key>
            <Key color={AGENTS_COLOR}>Agents {usd(headline.agentsMillicents)}</Key>
            <span style={{ marginLeft: "auto" }}>The headline is always month to date. The ledger follows the range above.</span>
          </Legend>
        </div>
      )}
      {headline.agentsMillicents === null && (
        <Note>Agent spend could not be loaded, so these figures cover developers only.</Note>
      )}
      {headline.incomplete && (
        <Note>
          Some spend ran on a lane with no Claude list equivalent, so the comparison covers part of the bill rather
          than guess.
        </Note>
      )}
    </>
  );
}

function ActivationSection({ ledger, now }: { ledger: Ledger; now: Date }) {
  const { activation, activationError } = ledger;
  const active = activation?.filter((member) => activationStatus(member, now).tone === "active").length ?? 0;
  return (
    <Sec k="03" title="Activation" fact="by login, not by spend" testId="usage-activation">
      {activationError ? (
        <p className="fact" data-testid="activation-error">
          Activation could not be loaded. Spend figures above are unaffected.
        </p>
      ) : activation === null ? (
        <Note>Loading activation</Note>
      ) : (
        <Fold title="Members" count={`${activation.length} · ${active} active this week`} defaultOpen>
          <div className="tbl" data-testid="activation-roster">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Status</th>
                  <th>Signed in from</th>
                  <th className="num">Last used</th>
                </tr>
              </thead>
              <tbody>
                {activation.map((member) => {
                  const status = activationStatus(member, now);
                  const color =
                    status.tone === "active"
                      ? "var(--accent-active)"
                      : status.tone === "quiet"
                        ? "var(--honesty)"
                        : "var(--text-quiet)";
                  return (
                    <tr key={member.userId}>
                      <td>
                        {memberDisplayName(member) === "Former member" ? member.userId : memberDisplayName(member)}
                        {member.displayName && member.email && <span className="sub">{member.email}</span>}
                      </td>
                      <td style={{ color }}>{status.label}</td>
                      <td className="fact">
                        {member.clients.length === 0 ? (
                          <Quiet>No sign-ins</Quiet>
                        ) : (
                          member.clients.map((client) => CLIENT_LABELS[client] ?? client).join(" · ")
                        )}
                      </td>
                      <td className="num fact">{member.lastUsedAt ? new Date(member.lastUsedAt).toISOString().slice(0, 10) : "Never"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Note>
            Status comes from device sign-ins and API use, not from spend, so a developer with a quiet week still reads
            as active on the day they last worked. Tokens minted before sign-in apps were recorded show as an unknown
            app.
          </Note>
        </Fold>
      )}
    </Sec>
  );
}
